import React, { useEffect } from "react";
import type { TableColumnType } from "antd";
import {
  StyledTable,
  TableContainer,
} from "../../../components/tables/styledComponents";
import { EngagementBannerList } from "./EngagementBanner";
import {
  CellType,
  ColumnType,
  TableCellType,
} from "../../../models/tableModels";
import { CustomTableCell } from "../../../components/tables/CustomTableCell";
import {
  EngagementDataSource,
  EngagementRecord,
  EngagementSubthemeMetric,
} from "../../../models/stewardshipModels";
import { buildEngagementBannerList } from "../utils";
import { useEngagementSubThemeMetrics } from "../../../hooks/useEngagementSubThemeMetrics";

const columns: TableColumnType<EngagementDataSource>[] = [
  {
    title: "Engagement Sub-Theme",
    dataIndex: "theme",
    key: "theme",
    fixed: "left",
  },
  {
    title: "Managers",
    dataIndex: "managers",
    key: "managers",
  },
  {
    title: "Corporate Issues",
    dataIndex: "corporateIssues",
    key: "corporateIssues",
  },
  {
    title: "Corporate Objectives",
    dataIndex: "corporateObjectives",
    key: "corporateObjectives",
  },
  { title: "Priority", dataIndex: "priority", key: "priority" },
  { title: "Momentum", dataIndex: "momentum", key: "momentum" },
];

export const SCREENER_COLUMN_TYPES: ColumnType[] = [
  { type: TableCellType.AVATAR, columnKey: "theme" },
  { type: TableCellType.AVATAR_STACK, columnKey: "managers" },
  { type: TableCellType.NUMBER, columnKey: "corporateIssues" },
  { type: TableCellType.NUMBER, columnKey: "corporateObjectives" },
  { type: TableCellType.DROPDOWN, columnKey: "priority" },
  { type: TableCellType.DROPDOWN, columnKey: "momentum" },
  { type: TableCellType.TITLE, columnKey: "theme" },
  { type: TableCellType.DROPDOWN, columnKey: "priority" },
  { type: TableCellType.DROPDOWN, columnKey: "momentum" },
];

export const EngagementTrackerTable: React.FC<{
  engagementRecords: EngagementRecord[];
  engagementSubThemeMetrics: EngagementSubthemeMetric[];
  companyId: string;
}> = ({ engagementRecords, engagementSubThemeMetrics, companyId }) => {
  const { buildDataSource, dataSource } =
    useEngagementSubThemeMetrics(companyId);

  const engagementBanners = buildEngagementBannerList(engagementRecords);

  useEffect(() => {
    buildDataSource(engagementRecords, engagementSubThemeMetrics);
  }, [engagementRecords, engagementSubThemeMetrics]);

  return (
    <TableContainer>
      <StyledTable
        dataSource={dataSource}
        hasEveryCellBorderBottom={true}
        pagination={false}
        scroll={{ y: "min(50vh, 550px)", x: "max-content" }}
        expandable={{
          expandedRowRender: (record): JSX.Element => {
            const matchingBanners = engagementBanners.filter(
              (banner) =>
                banner.subTheme === (record as EngagementDataSource).key
            );
            return <EngagementBannerList engagements={matchingBanners} />;
          },

          rowExpandable: (record) =>
            engagementBanners.some((banner) => banner.subTheme === record.key),
        }}
      >
        {columns.map((column) => (
          <StyledTable.Column
            key={column.key}
            dataIndex={column.dataIndex}
            title={column.title}
            fixed={column.fixed}
            render={(value: CellType): JSX.Element => (
              <CustomTableCell
                value={value}
                type={
                  SCREENER_COLUMN_TYPES.find(
                    (col) => col.columnKey === column.dataIndex
                  )?.type || TableCellType.TEXT
                }
              />
            )}
          />
        ))}
      </StyledTable>
    </TableContainer>
  );
};
